import { useCallback, useEffect, useRef, useState } from "react";
import { transcribeDictation } from "../interfaces/aiFilters";

/**
 * Dictation for a text field: record locally, transcribe server-side.
 *
 * The browser's own `SpeechRecognition` was the obvious route and doesn't hold up — Chrome
 * implements it by shipping the audio to a Google endpoint keyed to Chrome builds, so Brave
 * and the other Chromium forks fail every attempt with a network error while still showing
 * the mic as granted. `MediaRecorder` is available everywhere and behaves the same, and the
 * Whisper model behind `/ai-filters/transcribe` handles French place and trade names better
 * than the browser recognizer did.
 */
export type DictationStatus = "idle" | "recording" | "transcribing";

/** Long enough for any description the bar accepts, short enough that a forgotten mic stops itself. */
const MAX_RECORDING_MS = 60_000;

/** Below this a recording holds a container header and no speech — see the same floor server-side. */
const MIN_BLOB_BYTES = 2048;

const supportsRecording = (): boolean =>
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;

/** Chromium records webm/opus, Safari only mp4 — let the browser use the first one it knows. */
const pickMimeType = (): string | undefined => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
    return candidates.find(type => MediaRecorder.isTypeSupported?.(type));
};

const micErrorMessage = (err: any): string => {
    switch (err?.name) {
        case "NotAllowedError":
        case "SecurityError":
            return "Accès au micro refusé. Autorisez-le dans les réglages du navigateur.";
        case "NotFoundError":
        case "OverconstrainedError":
            return "Aucun micro détecté sur cet ordinateur.";
        case "NotReadableError":
            return "Le micro est déjà utilisé par une autre application.";
        default:
            return "Le micro n'a pas pu démarrer.";
    }
};

const transcriptionErrorMessage = (err: any): string => {
    switch (err?.response?.data?.error) {
        case "TOO_MANY_REQUESTS":
            return "Trop de dictées d'affilée. Patientez une minute.";
        case "AUDIO_EMPTY":
            return "Je n'ai rien entendu. Réessayez en parlant plus près du micro.";
        case "AUDIO_TOO_LARGE":
            return "Dictée trop longue. Faites une phrase plus courte.";
        case "AI_UNAVAILABLE":
            return "La dictée n'est pas disponible pour le moment.";
        default:
            return "La transcription a échoué. Réessayez.";
    }
};

interface UseVoiceDictationOptions {
    /** Receives the field's text at the start of the dictation, followed by what was said. */
    onTranscript: (text: string) => void;
}

export function useVoiceDictation({ onTranscript }: UseVoiceDictationOptions) {
    const [status, setStatus] = useState<DictationStatus>("idle");
    const [error, setError] = useState("");

    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The text already in the field when recording started — what was said is appended to it.
    const baseRef = useRef("");
    // Set when the component goes away mid-recording: the audio is then dropped rather than
    // transcribed into a field that no longer exists.
    const abandonedRef = useRef(false);

    const onTranscriptRef = useRef(onTranscript);
    useEffect(() => {
        onTranscriptRef.current = onTranscript;
    });

    const releaseRecorder = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        recorderRef.current?.stream.getTracks().forEach(track => track.stop());
        recorderRef.current = null;
    }, []);

    const start = useCallback(
        async (baseText: string = "") => {
            if (recorderRef.current || !supportsRecording()) return;

            setError("");
            baseRef.current = baseText.trim();
            abandonedRef.current = false;

            let stream: MediaStream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (err) {
                setError(micErrorMessage(err));
                return;
            }

            const mimeType = pickMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            chunksRef.current = [];

            recorder.ondataavailable = event => {
                if (event.data.size > 0) chunksRef.current.push(event.data);
            };

            recorder.onstop = async () => {
                const type = recorder.mimeType || mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type });
                chunksRef.current = [];
                releaseRecorder();

                if (abandonedRef.current) return;
                if (blob.size < MIN_BLOB_BYTES) {
                    setStatus("idle");
                    setError("Je n'ai rien entendu. Réessayez en parlant plus près du micro.");
                    return;
                }

                setStatus("transcribing");
                try {
                    const text = (await transcribeDictation(blob)).trim();
                    if (abandonedRef.current) return;
                    if (!text) {
                        setError("Je n'ai rien entendu. Réessayez en parlant plus près du micro.");
                    } else {
                        onTranscriptRef.current([baseRef.current, text].filter(Boolean).join(" "));
                    }
                } catch (err) {
                    if (!abandonedRef.current) setError(transcriptionErrorMessage(err));
                } finally {
                    if (!abandonedRef.current) setStatus("idle");
                }
            };

            recorder.start();
            recorderRef.current = recorder;
            setStatus("recording");
            timerRef.current = setTimeout(() => {
                if (recorderRef.current?.state === "recording") recorderRef.current.stop();
            }, MAX_RECORDING_MS);
        },
        [releaseRecorder]
    );

    const stop = useCallback(() => {
        if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    }, []);

    const toggle = useCallback(
        (baseText: string = "") => {
            if (recorderRef.current) stop();
            else void start(baseText);
        },
        [start, stop]
    );

    // Unmounting has to hand the mic back, or the browser keeps showing the page as recording.
    useEffect(
        () => () => {
            abandonedRef.current = true;
            if (recorderRef.current?.state === "recording") recorderRef.current.stop();
            releaseRecorder();
        },
        [releaseRecorder]
    );

    return { supported: supportsRecording(), status, error, start, stop, toggle };
}
